import { isValid } from 'date-fns';
import { AnySchema, NumberSchema, StringSchema } from 'yup';

import { anyNestedChild, toISODateString } from '../utils';

// this type is currently not exported by yup -> should be the same as in the package
type TransformFunction<T extends AnySchema> = (this: T, value: any, originalValue: any, schema: T) => any;

/**
 * NumberSchema doesn't allow empty string, undefined or null values -
 * With this transformation it's allowed to provide those by transforming them into undefined
 */
export const transformToOptionalNumber: TransformFunction<NumberSchema> = (value, originalValue, context) => {
  if (context.isType(value)) return value;
  if (typeof originalValue === 'string' && originalValue.trim() === '') return undefined;
  if (originalValue == null) return undefined;
  return isNaN(originalValue) ? NaN : value;
};

/**
 * NumberSchema does not properly transform an NaN value (e. g. by providing an empty string) to null
 * With this transformation NaN values will be transformed to null
 */
export const transformNaNToNull: TransformFunction<NumberSchema<number | null | undefined>> = (value, _originalValue, context) => {
  return typeof value === 'number' && isNaN(value) ? null : value;
};

/**
 * StringSchema does not handle empty string properly -
 * With this transformation empty strings will be transformed into undefined (e.g. for an ID that can't be an empty string)
 * Otherwise just return the value as it is
 */
export const transformEmptyStringToUndefined: TransformFunction<StringSchema> = (value, originalValue) => {
  if (typeof originalValue === 'string' && originalValue.trim() === '') return undefined;

  return value;
};

export const transformToOptionalObject: TransformFunction<AnySchema> = (value) => {
  if (!Object.values(value).some((x) => x)) return undefined;

  return value;
};

export const transformDateToIsoDateString: TransformFunction<StringSchema<any>> = (value, originalValue) => {
  if (originalValue == null) return value;
  if (!(originalValue instanceof Date)) return value;

  const date = new Date(originalValue);
  if (!isValid(date)) return value;

  return toISODateString(date);
}

export const transformToNullableObject: TransformFunction<AnySchema> = (value) => {
  if (anyNestedChild(value)) {
    return value;
  } else {
    return null;
  }
}
